import { useMemo } from "react";

import { BookingAnalyticsEvent } from "@/constants/analytics/Analytics";
import { BookingStepBaseTestIDMap } from "@/constants/Booking";
import { BookingTestID } from "@/constants/TestID";
import useBookingAnalytics from "@/hooks/useBookingAnalytics";
import useBookingNavigation from "@/hooks/useBookingNavigation";
import { useGetProviderTypeAttendeesQuery } from "@/store/api/schedule/booking";
import { useAppSelector } from "@/store/hooks";
import { useAppUserSelector } from "@/store/reducers/appUser";
import {
  selectAppointmentTypeGroup,
  selectPatientId,
  selectProviderType,
} from "@/store/reducers/booking";
import AppointmentAvatarCard from "@/ui/components/schedule/booking/AppointmentAvatarCard";
import BookingModalFooter from "@/ui/components/schedule/booking/BookingModalFooter";
import type { BaseBookingStepProps } from "@/ui/components/schedule/booking/BookingStepMap";
import BookingStepTitle from "@/ui/components/schedule/booking/BookingStepTitle";
import { Button, FAIconName } from "@/ui/core";
import { FlatList, HStack } from "@/ui/layout";
import { getRequiredAttendees } from "@/utils/booking.util";
import { joinTestIDs } from "@/utils/string.util";

const AppointmentAttendeesStep = ({ step }: BaseBookingStepProps) => {
  const baseTestID = BookingStepBaseTestIDMap[step];

  const patientExternalId = useAppSelector(selectPatientId);
  const typeGroup = useAppSelector(selectAppointmentTypeGroup);
  const bookingProvider = useAppSelector(selectProviderType);
  const { externalId: appUserId } = useAppUserSelector();

  const { trackEvent } = useBookingAnalytics();

  const { data, isLoading } = useGetProviderTypeAttendeesQuery({
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- grandfathered in, should probably use a { skip: isEmpty(...) || isEmpty(...)
    patientExternalId: patientExternalId!,
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- grandfathered in, should probably use a { skip: isEmpty(...) || isEmpty(...)
    typeGroup: typeGroup!,
  });

  const {
    onBackwardNavigation,
    onForwardNavigation,
    onOptionalAttendeesNavigation,
  } = useBookingNavigation({
    currentStep: step,
  });

  const handleForwardNavigation = () => {
    onForwardNavigation();
  };

  const attendees = useMemo(() => {
    return getRequiredAttendees({
      attendees: data,
      providerType: bookingProvider,
    });
  }, [bookingProvider, data]);

  const isOptionalAttendeesEnabled = useMemo(() => {
    return data?.some((attendee) => !attendee.isRequired);
  }, [data]);

  const handleOptionalAttendeesNavigation = () => {
    trackEvent({
      event: BookingAnalyticsEvent.AddOptionalAttendees,
      source: step,
    });
    onOptionalAttendeesNavigation();
  };

  const ListFooterComponent = isOptionalAttendeesEnabled ? (
    <HStack paddingVertical={24}>
      <Button
        accessibilityLabel="Add someone else"
        leftIcon={FAIconName.PlusCircle}
        onPress={handleOptionalAttendeesNavigation}
        testID={joinTestIDs(baseTestID, BookingTestID.AddAttendee)}
        title="Add someone else"
        variant="secondary"
      />
    </HStack>
  ) : null;

  const ListHeaderComponent = <BookingStepTitle step={step} />;

  return (
    <>
      <FlatList
        data={attendees}
        ListFooterComponent={ListFooterComponent}
        ListHeaderComponent={ListHeaderComponent}
        renderItem={({ item }) => {
          const { subtitle, userId, userName, userType } = item;
          const isAppUser = userId === appUserId;
          return (
            <AppointmentAvatarCard
              isAppUser={isAppUser}
              isDisabled
              subtitle={subtitle}
              userId={userId}
              userName={userName}
              userType={userType}
            />
          );
        }}
        testID={baseTestID}
      />
      <BookingModalFooter
        isLoading={isLoading}
        onBackwardPress={onBackwardNavigation}
        onForwardPress={handleForwardNavigation}
        step={step}
      />
    </>
  );
};

export default AppointmentAttendeesStep;
